import styled from "styled-components/macro";

/**
 * @typedef {{
 *   children: React.ReactNode,
 *   onClick?: () => void,
 *   disabled?: boolean,
 *   primary?: boolean,
 *   secondary?: boolean,
 *   tertiary?: boolean,
 *   secondaryGrey?: boolean,
 *   tertiaryGrey?: boolean,
 *   small?: boolean,
 *   iconSize?: number,
 * }} ButtonProps
 * */

export const Button = styled.button(
  /**
   * @param {ButtonProps} props
   * @returns {import("styled-components").CSSObject}
   */
  (props) => {
    const cssVariables = {
      "--button-text-color": "currentColor",
      "--button-background-color": "transparent",
      "--button-background-hover-color": "var(--menu-color-hover)",
      "--button-background-active-color": "var(--menu-color-active)",
      "--button-border-color": "transparent",
      "--button-border-hover-color": "transparent",
      "--button-border-active-color": "transparent",
    };

    const buttonResetStyles = {
      boxSizing: "border-box",
      margin: 0,
      whiteSpace: "nowrap",
      fontFamily: "inherit",
      lineHeight: "normal",
      userSelect: "none",
      appRegion: "no-drag",
    };

    const baseButtonStyles = {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      gap: 4,
      position: "relative",
      isolation: "isolate",
      border: "none",
      backgroundColor: "transparent",
      color: "var(--button-text-color)",
      minWidth: 40,
      fontWeight: 500,
      cursor: "pointer",
    };

    const buttonStylesMedium = {
      fontSize: 16,
      padding: "8px 10px",
      height: 40,
    };

    const buttonStylesSmall = {
      fontSize: 14,
      padding: "8px 10px",
    };

    const buttonStyles = props.small ? { ...baseButtonStyles, ...buttonStylesSmall } : { ...baseButtonStyles, ...buttonStylesMedium };

    const pseudoElementStyles = {
      content: '""',
      boxSizing: "border-box",
      opacity: 1,
      borderRadius: 30,
      borderWidth: 2,
      borderColor: "var(--button-border-color)",
      borderStyle: "solid",
      backgroundColor: "var(--button-background-color)",
      position: "absolute",
      height: "100%",
      width: "100%",
      zIndex: -1,
      transitionProperty: "width, height, background-color, border-radius, opacity",
      transitionDuration: "0.2s",
      transitionEasingFunction: "ease-in-out",
    };

    if (props.small) {
      Object.assign(cssVariables, {
        "--button-text-color": "var(--grey-0)",
        "--button-background-color": "transparent",
        "--button-background-hover-color": "color-mix(in srgb, var(--button-text-color) 10%, transparent)",
        "--button-background-active-color": "color-mix(in srgb, var(--button-text-color) 20%, transparent)",
      });

      Object.assign(pseudoElementStyles, {
        opacity: 1,
        borderRadius: 10,
      });
    }

    if (props.primary) {
      Object.assign(cssVariables, {
        "--button-text-color": "var(--cta-text-color)",
        "--button-background-color": "var(--primary-cta)",
        "--button-background-hover-color": "var(--primary-cta-hover)",
        "--button-background-active-color": "var(--primary-cta-active)",
      });

      Object.assign(pseudoElementStyles, {
        opacity: 1,
        borderRadius: 10,
      });
    }

    if (props.secondary) {
      Object.assign(cssVariables, {
        "--button-text-color": "var(--primary-cta-high-contrast)",
        "--button-background-color": "transparent",
        "--button-background-hover-color": "color-mix(in srgb, var(--button-text-color) 10%, transparent)",
        "--button-background-active-color": "color-mix(in srgb, var(--button-text-color) 20%, transparent)",
        "--button-border-color": "var(--button-text-color)",
        "--button-border-hover-color": "var(--button-text-color)",
        "--button-border-active-color": "var(--button-text-color)",
      });

      Object.assign(pseudoElementStyles, {
        opacity: 1,
        borderRadius: 10,
      });
    }

    if (props.tertiary) {
      Object.assign(cssVariables, {
        "--button-text-color": "var(--primary-cta)",
        "--button-background-color": "transparent",
        "--button-background-hover-color": "color-mix(in srgb, var(--button-text-color) 10%, transparent)",
        "--button-background-active-color": "color-mix(in srgb, var(--button-text-color) 20%, transparent)",
      });

      Object.assign(pseudoElementStyles, {
        opacity: 1,
        borderRadius: 10,
      });
    }

    if (props.tertiaryBorder) {
      Object.assign(cssVariables, {
        "--button-text-color": "var(--primary-cta-high-contrast)",
        "--button-background-color": "transparent",
        "--button-background-hover-color": "var(--primary-cta-transparent)",
        "--button-background-active-color": "var(--primary-cta-transparent)",
        "--button-border-color": "var(--primary-cta-transparent)",
        "--button-border-hover-color": "transparent",
        "--button-border-active-color": "transparent",
      });

      Object.assign(pseudoElementStyles, {
        opacity: 1,
        borderRadius: 10,
      });
    }

    if (props.secondaryGrey) {
      Object.assign(cssVariables, {
        "--button-text-color": "var(--grey-0)",
        "--button-background-color": "transparent",
        "--button-background-hover-color": "color-mix(in srgb, var(--button-text-color) 10%, transparent)",
        "--button-background-active-color": "color-mix(in srgb, var(--button-text-color) 20%, transparent)",
      });

      Object.assign(pseudoElementStyles, {
        opacity: 1,
        borderRadius: 10,
      });
    }

    if (props.tertiaryGrey) {
      Object.assign(cssVariables, {
        "--button-text-color": "var(--grey-1)",
        "--button-background-color": "transparent",
        "--button-background-hover-color": "color-mix(in srgb, var(--button-text-color) 10%, transparent)",
        "--button-background-active-color": "color-mix(in srgb, var(--button-text-color) 20%, transparent)",
      });

      Object.assign(pseudoElementStyles, {
        opacity: 1,
        borderRadius: 10,
      });
    }

    const svgStyles = {
      fill: "currentColor",
      path: {
        fill: "currentColor",
      },
    };

    if (props.iconSize) {
      Object.assign(buttonStyles, {
        padding: "8px",
      });
      Object.assign(svgStyles, {
        width: `${props.iconSize}px`,
        height: `${props.iconSize}px`,
      });
    }

    return {
      ...cssVariables,
      ...buttonResetStyles,
      ...buttonStyles,

      "&:active": {
        backgroundColor: "transparent",
      },

      "&:before": pseudoElementStyles,

      "&:hover:before": {
        borderRadius: 10,
        backgroundColor: "var(--button-background-hover-color)",
        borderColor: "var(--button-border-hover-color)",
        opacity: 1,
      },

      "&:active:before": {
        borderRadius: 6,
        height: "calc(100% - 6px)",
        width: "calc(100% - 6px)",
        backgroundColor: "var(--button-background-active-color)",
        borderColor: "var(--button-border-active-color)",
        opacity: 1,
      },

      "&:focus-visible": {
        outline: "2px solid var(--primary-cta)",
        outlineOffset: 2,
        borderRadius: 10,
      },

      "&:focus-visible:before": {
        borderRadius: 10,
        backgroundColor: "var(--button-background-hover-color)",
        borderColor: "var(--button-border-hover-color)",
        opacity: 1,
      },

      "&:disabled": {
        pointerEvents: "none",
        opacity: 0.5,
      },

      "> svg": svgStyles,
    };
  }
);

Button.displayName = "Button";
